import type { Schemas } from '@kanos/api-client';
import { api, failure } from '@kanos/web-core';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from './queries';

export type Notification = Schemas['Notification'];
export type NotificationPreference = Schemas['NotificationPreference'];

const inboxKey = (tenantId: string) => [...keys.all(tenantId), 'inbox'] as const;

/** How often the unread messages are looked at again (`FirmPolling`). */
export const UNREAD_POLL_MS = 30_000;

/**
 * The person's messages (ADR-042), newest first. The unread ones - the bell's - are kept current
 * once for the app (`FirmPolling`); the full list where it is shown, every half minute.
 */
export const notificationsQuery = (tenantId: string, unread = false) =>
  queryOptions({
    queryKey: [...inboxKey(tenantId), unread] as const,
    queryFn: async (): Promise<Schemas['NotificationList']> => {
      const { data, error, response } = await api.GET('/api/v1/me/notifications', {
        params: { query: unread ? { unread: 'true' } : {} },
      });
      if (!data) throw failure('listNotifications', error, response.status);
      return data;
    },
    ...(unread ? {} : { refetchInterval: UNREAD_POLL_MS }),
  });

/** One message read - or, without an id, all of them. */
export function useReadNotifications(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | null) => {
      const { error, response } = id
        ? await api.PUT('/api/v1/me/notifications/{notificationId}/read', {
            params: { path: { notificationId: id } },
          })
        : await api.PUT('/api/v1/me/notifications/read');
      if (!response.ok) {
        throw failure(id ? 'readNotification' : 'readAllNotifications', error, response.status);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: inboxKey(tenantId) }),
  });
}

export const preferencesQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'notification-preferences'] as const,
    queryFn: async (): Promise<NotificationPreference[]> => {
      const { data, error, response } = await api.GET('/api/v1/me/notification-preferences');
      if (!data) throw failure('getNotificationPreferences', error, response.status);
      return data;
    },
  });

export function useSetPreferences(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (choices: NotificationPreference[]) => {
      const { data, error, response } = await api.PUT('/api/v1/me/notification-preferences', {
        body: choices,
      });
      if (!data) throw failure('setNotificationPreferences', error, response.status);
      return data;
    },
    onSuccess: (data) => queryClient.setQueryData(preferencesQuery(tenantId).queryKey, data),
  });
}
